import React from 'react';

/**
 * GoldenCommonsWelcome - Welcome page for Golden Commons (Each One, Teach One)
 *
 * Light color palette to distinguish from active learning environment
 * Explains capacity for knowledge circulation, Ubuntu principle
 * Notes: Partially active (Learning Circles exist, teach-back coming)
 */
function GoldenCommonsWelcome({ onBackToSIFISO, onEnterLearningCircles }) {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F5F5F0',
      color: '#2A2A2A'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '50px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '30px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                color: '#D4AF37',
                marginBottom: '10px',
                letterSpacing: '-0.5px'
              }}>
                GOLDEN COMMONS
              </h1>
              <p style={{
                fontSize: '1.2rem',
                fontStyle: 'italic',
                color: '#6B6B6B'
              }}>
                Each One, Teach One
              </p>
            </div>
            <button
              onClick={onBackToSIFISO}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Return to SIFISO
            </button>
          </div>

          <div style={{
            height: '3px',
            background: 'linear-gradient(to right, #D4AF37, transparent)',
            marginBottom: '20px'
          }} />

          <p style={{
            fontSize: '1.4rem',
            fontWeight: '600',
            color: '#2A2A2A',
            marginBottom: '15px'
          }}>
            Knowledge Circulation and Preservation
          </p>
          <p style={{
            fontSize: '1rem',
            color: '#6B6B6B',
            lineHeight: '1.7',
            maxWidth: '700px'
          }}>
            The capacity to pass knowledge forward so it does not die with you. Ubuntu principle:
            teach-back, learning circles, shared stewardship.
          </p>
        </header>

        {/* Main Content */}
        <main>
          {/* Status Box */}
          <div style={{
            backgroundColor: '#FFFEF5',
            border: '2px solid #D4AF37',
            borderRadius: '12px',
            padding: '30px',
            marginBottom: '40px'
          }}>
            <div style={{
              fontSize: '0.85rem',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#D4AF37',
              marginBottom: '15px'
            }}>
              Partially Active
            </div>
            <p style={{
              fontSize: '1.05rem',
              color: '#2A2A2A',
              lineHeight: '1.7',
              marginBottom: '15px'
            }}>
              Golden Commons is the fifth finger—the one that closes the loop. Learning Circles
              are active now, allowing small groups to learn together. Teach-back mechanisms
              and shared artifacts are being designed.
            </p>
            <p style={{
              fontSize: '0.95rem',
              color: '#6B6B6B',
              lineHeight: '1.7',
              marginBottom: '20px'
            }}>
              This is Ubuntu in practice: I am because we are. Knowledge that stays locked
              in individuals dies. Knowledge that circulates regenerates.
            </p>

            {/* Call to Action */}
            <button
              onClick={onEnterLearningCircles}
              style={{
                padding: '14px 28px',
                backgroundColor: '#D4AF37',
                color: '#2A2A2A',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginTop: '10px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#C49B2E'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#D4AF37'}
            >
              Enter Learning Circles →
            </button>
          </div>

          {/* What This Builds */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What This Capacity Builds
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Teaching ability (you learn by teaching)',
                  'Peer knowledge exchange (learning from each other)',
                  'Artifact creation (documenting what you know)',
                  'Local adaptation (making knowledge relevant to context)',
                  'Intergenerational transfer (preserving knowledge for those who come after)'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '15px' : '0',
                    fontSize: '1rem',
                    lineHeight: '1.6',
                    color: '#2A2A2A'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.3rem',
                      fontWeight: '700'
                    }}>
                      •
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Position in Loop */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              Position in the Loop
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px'
              }}>
                Golden Commons closes the loop. It's where capacity that has been built, tested,
                and applied flows back into the system so others can learn it too.
              </p>
              <div style={{
                backgroundColor: '#F5F5F0',
                padding: '20px',
                borderRadius: '8px',
                borderLeft: '4px solid #D4AF37'
              }}>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#2A2A2A',
                  lineHeight: '1.7',
                  fontStyle: 'italic',
                  margin: 0
                }}>
                  "Without Golden Commons, the Golden Hand is just individual learning. With it,
                  learning becomes regenerative. What you learned doesn't die when you stop.
                  It circulates. It renews. It belongs to the community, not just to you."
                </p>
              </div>
            </div>
          </section>

          {/* What's Active Now */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What's Active Now
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <p style={{
                fontSize: '1rem',
                color: '#2A2A2A',
                lineHeight: '1.7',
                marginBottom: '20px',
                fontWeight: '600'
              }}>
                Learning Circles (Active)
              </p>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Create or join small learning circles (max 12 members)',
                  'Share your current level progress with circle members',
                  'See what others are learning (no scores, no rankings)',
                  'Learn together in small, human-scale groups',
                  'Ubuntu principle: "I am learning" becomes "We are learning"'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      ✓
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* What's Coming */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What's Being Designed
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Teach-back mechanism (explain what you learned in your own words)',
                  'Shared artifact creation (document knowledge for others)',
                  'Local context adaptation (make concepts relevant to your community)',
                  'Peer validation (learn by reviewing each other\'s understanding)',
                  'Feedback loop into Golden Learn (excellent teach-backs become learning content)'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      →
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* What This Is Not */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#2A2A2A',
              marginBottom: '20px'
            }}>
              What Golden Commons Is Not
            </h2>
            <div style={{
              backgroundColor: 'white',
              border: '1px solid #E5E5D5',
              borderRadius: '10px',
              padding: '25px'
            }}>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Not a social network',
                  'Not a discussion forum or chat room',
                  'Not user-generated content for platform growth',
                  'Not ranked contributors or leaderboards',
                  'Not extractable or monetizable by others'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: idx < 4 ? '12px' : '0',
                    fontSize: '0.95rem',
                    lineHeight: '1.6',
                    color: '#6B6B6B'
                  }}>
                    <span style={{
                      color: '#D4AF37',
                      marginRight: '12px',
                      fontSize: '1.1rem'
                    }}>
                      ✗
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Principle */}
          <section>
            <div style={{
              backgroundColor: '#2A2A2A',
              color: '#F5F5F0',
              borderRadius: '10px',
              padding: '30px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '1.2rem',
                fontWeight: '600',
                lineHeight: '1.7',
                margin: 0
              }}>
                Individual learning dies. Collective learning survives.
                <br />
                <span style={{ color: '#D4AF37', fontStyle: 'italic' }}>
                  Ubuntu: I am because we are.
                </span>
              </p>
            </div>
          </section>
        </main>

        {/* Footer Note */}
        <footer style={{
          marginTop: '60px',
          paddingTop: '30px',
          borderTop: '1px solid #E5E5D5',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: '#6B6B6B',
            lineHeight: '1.6'
          }}>
            Golden Commons is where the loop closes and renews.
            <br />
            Learning Circles are active now. Teach-back and shared artifacts are being designed with care.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default GoldenCommonsWelcome;
